import { useState, useRef, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Upload, Trash2, Loader2, Image, Sparkles, AlertCircle, Check, MousePointerClick } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';

interface RealtorAd {
  id: string;
  image_url: string;
  is_active: boolean;
  created_at: string;
  click_count: number;
  ad_type: 'buyer' | 'seller';
}

interface RealtorAdManagerProps {
  realtorProfileId: string;
  realtorName: string;
  photoUrl: string | null;
  logoUrl: string | null;
  adImageUrl: string | null;
  onAdImageChange: (url: string | null) => void;
}

type AdType = 'buyer' | 'seller';

export function RealtorAdManager({
  realtorProfileId,
  realtorName,
  photoUrl,
  logoUrl,
  adImageUrl,
  onAdImageChange,
}: RealtorAdManagerProps) {
  const { toast } = useToast();
  const [uploading, setUploading] = useState<AdType | null>(null);
  const [generating, setGenerating] = useState<AdType | null>(null);
  const [buyerAds, setBuyerAds] = useState<RealtorAd[]>([]);
  const [sellerAds, setSellerAds] = useState<RealtorAd[]>([]);
  const [loadingAds, setLoadingAds] = useState(true);
  const buyerFileInputRef = useRef<HTMLInputElement>(null);
  const sellerFileInputRef = useRef<HTMLInputElement>(null);

  const hasRequiredInfo = realtorName.trim() !== '';

  // Fetch existing ads for this realtor
  const fetchAds = async () => {
    if (!realtorProfileId) {
      setLoadingAds(false);
      return;
    }
    
    setLoadingAds(true);
    const { data, error } = await supabase
      .from('realtor_ads')
      .select('*')
      .eq('realtor_profile_id', realtorProfileId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching ads:', error);
      setBuyerAds([]);
      setSellerAds([]);
    } else if (data && data.length > 0) {
      const typedAds = data as RealtorAd[];
      setBuyerAds(typedAds.filter(ad => ad.ad_type === 'buyer'));
      setSellerAds(typedAds.filter(ad => ad.ad_type === 'seller'));
    } else if (adImageUrl) {
      // No ads in new table, but we have a legacy ad_image_url - migrate it as buyer ad
      const { data: insertedAd, error: insertError } = await supabase
        .from('realtor_ads')
        .insert({
          realtor_profile_id: realtorProfileId,
          image_url: adImageUrl,
          is_active: true,
          ad_type: 'buyer',
        })
        .select()
        .single();

      if (!insertError && insertedAd) {
        setBuyerAds([insertedAd as RealtorAd]);
        setSellerAds([]);
      } else {
        console.error('Error migrating legacy ad:', insertError);
        setBuyerAds([]);
        setSellerAds([]);
      }
    } else {
      setBuyerAds([]);
      setSellerAds([]);
    }
    setLoadingAds(false);
  };

  useEffect(() => {
    if (realtorProfileId) {
      fetchAds();
    }
  }, [realtorProfileId]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>, adType: AdType) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid file type',
        description: 'Please upload an image file (PNG, JPG, WebP)',
        variant: 'destructive',
      });
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'Image must be under 2MB',
        variant: 'destructive',
      });
      return;
    }

    const currentAds = adType === 'buyer' ? buyerAds : sellerAds;

    setUploading(adType);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `realtor-ads/${realtorProfileId}/${adType}-ad-${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('realtor-assets')
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('realtor-assets')
        .getPublicUrl(fileName);

      // Check if we're at the limit for this type
      if (currentAds.length >= 3) {
        // Delete the oldest ad
        const oldestAd = currentAds[currentAds.length - 1];
        await supabase
          .from('realtor_ads')
          .delete()
          .eq('id', oldestAd.id);
      }

      // Insert new ad - active by default if first of this type
      const isFirst = currentAds.length === 0;
      await supabase
        .from('realtor_ads')
        .insert({
          realtor_profile_id: realtorProfileId,
          image_url: publicUrl,
          is_active: isFirst,
          ad_type: adType,
        });

      if (isFirst && adType === 'buyer') {
        onAdImageChange(publicUrl);
      }

      await fetchAds();

      toast({
        title: 'Image uploaded',
        description: `${adType === 'buyer' ? 'Buyer' : 'Seller'} ad image has been added.`,
      });
    } catch (error) {
      console.error('Error uploading ad image:', error);
      toast({
        title: 'Upload failed',
        description: 'Failed to upload image. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setUploading(null);
      if (adType === 'buyer' && buyerFileInputRef.current) {
        buyerFileInputRef.current.value = '';
      }
      if (adType === 'seller' && sellerFileInputRef.current) {
        sellerFileInputRef.current.value = '';
      }
    }
  };

  const handleRemove = async (adId: string, adType: AdType) => {
    const currentAds = adType === 'buyer' ? buyerAds : sellerAds;
    const adToRemove = currentAds.find(a => a.id === adId);
    if (!adToRemove) return;

    const { error } = await supabase
      .from('realtor_ads')
      .delete()
      .eq('id', adId);

    if (error) {
      toast({
        title: 'Error',
        description: 'Failed to remove ad.',
        variant: 'destructive',
      });
      return;
    }

    // If we removed an active buyer ad, update legacy field
    if (adType === 'buyer') {
      const remainingBuyerAds = buyerAds.filter(a => a.id !== adId);
      const hasActiveBuyerAd = remainingBuyerAds.some(a => a.is_active);
      
      if (!hasActiveBuyerAd) {
        const firstActiveSeller = sellerAds.find(a => a.is_active);
        onAdImageChange(firstActiveSeller?.image_url || null);
        await supabase
          .from('realtor_profiles')
          .update({ ad_image_url: firstActiveSeller?.image_url || null })
          .eq('id', realtorProfileId);
      }
    }

    await fetchAds();
    toast({
      title: 'Ad removed',
      description: 'The ad image has been removed.',
    });
  };

  // Toggle active status (can have multiple active per type)
  const handleToggleActive = async (adId: string, adType: AdType) => {
    const currentAds = adType === 'buyer' ? buyerAds : sellerAds;
    const adToToggle = currentAds.find(a => a.id === adId);
    if (!adToToggle) return;

    const newActiveStatus = !adToToggle.is_active;

    await supabase
      .from('realtor_ads')
      .update({ is_active: newActiveStatus })
      .eq('id', adId);

    // Update legacy field with first active ad (prefer buyer, then seller)
    const updatedCurrentAds = currentAds.map(a => 
      a.id === adId ? { ...a, is_active: newActiveStatus } : a
    );
    
    const allBuyerAds = adType === 'buyer' ? updatedCurrentAds : buyerAds;
    const allSellerAds = adType === 'seller' ? updatedCurrentAds : sellerAds;
    
    const firstActiveAd = allBuyerAds.find(a => a.is_active) || allSellerAds.find(a => a.is_active);
    
    await supabase
      .from('realtor_profiles')
      .update({ ad_image_url: firstActiveAd?.image_url || null })
      .eq('id', realtorProfileId);

    onAdImageChange(firstActiveAd?.image_url || null);
    await fetchAds();

    toast({
      title: newActiveStatus ? 'Ad activated' : 'Ad deactivated',
      description: newActiveStatus 
        ? 'This ad will now be included in rotation.' 
        : 'This ad will no longer be shown.',
    });
  };

  const handleGenerateAI = async (adType: AdType) => {
    if (!hasRequiredInfo) {
      toast({
        title: 'Missing required information',
        description: 'Please enter the realtor name first.',
        variant: 'destructive',
      });
      return;
    }

    // Photo is no longer required: realtor ads can be generated as generic, non-person ads.

    setGenerating(adType);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const response = await supabase.functions.invoke('generate-realtor-ad', {
        body: { realtorProfileId, adType },
      });

      if (response.error) {
        throw response.error;
      }

      const data = response.data;

      if (data.ad_image_url) {
        await fetchAds();
        toast({
          title: 'Ad generated',
          description: `AI-generated ${adType} ad has been created.`,
        });
      } else {
        throw new Error(data.error || 'No image returned');
      }
    } catch (error) {
      console.error('Error generating ad:', error);
      toast({
        title: 'Generation failed',
        description: error instanceof Error ? error.message : 'Failed to generate ad. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setGenerating(null);
    }
  };

  const isLoading = uploading !== null || generating !== null;

  if (!realtorProfileId) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Please save your profile first to manage ads.
        </AlertDescription>
      </Alert>
    );
  }

  const renderAdSection = (
    adType: AdType,
    ads: RealtorAd[],
    fileInputRef: React.RefObject<HTMLInputElement>
  ) => {
    const canAddMore = ads.length < 3;
    const activeCount = ads.filter(a => a.is_active).length;
    const label = adType === 'buyer' ? 'Buyer' : 'Seller';
    const isGeneratingThis = generating === adType;
    const isUploadingThis = uploading === adType;

    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium">{label} Ads ({ads.length}/3)</h4>
          {ads.length > 0 && (
            <span className="text-xs text-muted-foreground">
              {activeCount} active • Click to toggle
            </span>
          )}
        </div>

        {loadingAds ? (
          <div className="flex items-center justify-center h-24">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : ads.length === 0 ? (
          <div className="w-full max-w-xs aspect-square border rounded-lg overflow-hidden bg-muted flex items-center justify-center">
            <div className="text-center p-4">
              <Image className="h-10 w-10 text-muted-foreground mx-auto mb-2" />
              <span className="text-sm text-muted-foreground">No {label.toLowerCase()} ads<br />1080×1080px</span>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {ads.map((ad) => (
              <div
                key={ad.id}
                className={cn(
                  "relative aspect-square border-2 rounded-lg overflow-hidden cursor-pointer transition-all",
                  ad.is_active 
                    ? "border-primary ring-2 ring-primary/20" 
                    : "border-muted hover:border-primary/50 opacity-60"
                )}
                onClick={() => handleToggleActive(ad.id, adType)}
              >
                <img
                  src={ad.image_url}
                  alt={`${label} ad`}
                  className="w-full h-full object-cover"
                />
                {ad.is_active && (
                  <div className="absolute top-2 left-2 bg-primary text-primary-foreground px-2 py-1 rounded text-xs font-medium flex items-center gap-1">
                    <Check className="h-3 w-3" />
                    Active
                  </div>
                )}
                {/* Click count badge */}
                <div className="absolute bottom-2 left-2 bg-background/90 text-foreground px-2 py-1 rounded text-xs font-medium flex items-center gap-1">
                  <MousePointerClick className="h-3 w-3" />
                  {ad.click_count || 0}
                </div>
                <Button
                  type="button"
                  variant="destructive"
                  size="icon"
                  className="absolute top-2 right-2 h-7 w-7"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(ad.id, adType);
                  }}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={(e) => handleUpload(e, adType)}
            className="hidden"
            id={`realtor-${adType}-ad-upload`}
          />

          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => handleGenerateAI(adType)}
            disabled={isLoading || !hasRequiredInfo || !canAddMore}
          >
            {isGeneratingThis ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                Generate {label} Ad
              </>
            )}
          </Button>

          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={isLoading || !canAddMore}
          >
            {isUploadingThis ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                Upload
              </>
            )}
          </Button>

          {!canAddMore && (
            <p className="text-xs text-muted-foreground self-center">
              Max 3. Remove one to add more.
            </p>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {!hasRequiredInfo && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Name is required to generate or display ads.
          </AlertDescription>
        </Alert>
      )}

      {/* Buyer Ads Section */}
      {renderAdSection('buyer', buyerAds, buyerFileInputRef)}

      {/* Seller Ads Section */}
      {renderAdSection('seller', sellerAds, sellerFileInputRef)}

      <p className="text-xs text-muted-foreground">
        Recommended: 1080×1080px (1:1). Active ads from both categories will rotate randomly on public pages.
      </p>
    </div>
  );
}
